import { ArcButton, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from "@src/core/ui";
import { selectFreehandShapeUponCreation, setFreehandShapeUponCreation, setZoneMapLocationUponCreation, useUpdateZoneMutation } from "@src/features/zones/redux";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { EditZoneStage2Props } from "./types";
import { ICreateZoneStage2Form } from '@src/features/zones/pages/create-zone/stage2/types';
import { useDispatch, useSelector } from "react-redux";
import { ZoneTypeName } from "@src/features/zones/config/zones-constants";
import { useNavigate } from "react-router-dom";
import { routesConfig } from "@src/core/routes";
import { ZoneType } from "@src/core/features";


export const EditZoneStage2Page: React.FC<EditZoneStage2Props> = (props) => {
  const { zone } = props;
  const { control, setValue, formState: { errors }, handleSubmit } = useForm<ICreateZoneStage2Form>({ 
    defaultValues: { 
      zoneType: zone.zoneType,
      zoneName: zone.name,
      zoneDescription: zone.description,
    } as ICreateZoneStage2Form
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [updateZone, { isLoading }] = useUpdateZoneMutation();

  const freehandShapeUponCreation = useSelector(selectFreehandShapeUponCreation);
  useEffect(() => {
    dispatch(setFreehandShapeUponCreation({
      id: zone.id,
      name: zone.name,
      type: zone.zoneType,
      polygonCoordinates: zone.polygonCoordinates,
    }));
    return () => {
      dispatch(setFreehandShapeUponCreation(null));
    };
  }, [zone]);

  const onCancel = () => {
    navigate(-1);
    dispatch(setZoneMapLocationUponCreation(null));
  };

  const onClear = () => {
    dispatch(setFreehandShapeUponCreation( {
      id: freehandShapeUponCreation?.id || zone.id,
      name: freehandShapeUponCreation?.name || zone.name,
      type: freehandShapeUponCreation?.type || ZoneType.TEMPORARY,
      polygonCoordinates: [],
    }));
  };

  const onSubmit = async (data: ICreateZoneStage2Form) => {
    try {

      await updateZone({ id: zone.id, 
        name: data.zoneName,
        description: data.zoneDescription, 
        zoneType: data.zoneType, 
        polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
      }).unwrap();
      dispatch(setZoneMapLocationUponCreation(null));
      navigate(routesConfig.zones); 
    } catch (error) {
      console.error('Failed to create zone:', error);
    }
  };

  

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcText.h4 fontWeight={500} marginTop={2}> DRAW ZONE </ArcText.h4>
        <ArcText.h5 sx={{color: 'text.secondary', marginTop: 1}}> Draw a shape on the map to set up a zone. </ArcText.h5>

        <ArcStack flex={1}>
          <ArcStack marginTop={3} spacing={2}>

            <Controller
              control={control}
              name="zoneType"
              render={({ field: { value, onChange } }) => (
                <ArcOutlinedSelect
                  label="Zone Type"
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    dispatch(setFreehandShapeUponCreation({
                      id: freehandShapeUponCreation?.id || zone.id,
                      name: freehandShapeUponCreation?.name || "",
                      type: e.target.value as ZoneType,
                      polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
                    }));
                  }}
                  error={Boolean(errors.zoneType)}
                  helperText={errors.zoneType?.message}
                >
                  {Object.entries(ZoneTypeName).map(([key, name]) => (
                    <ArcMenuItem key={key} value={key}>
                      {name}
                    </ArcMenuItem>
                  ))}
                </ArcOutlinedSelect>
              )}
            />

            <Controller
              name="zoneName"
              control={control}
              rules={{
                required: "Zone name is required",
                maxLength: { value: 20, message: "Maximum 20 characters allowed" },
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <ArcOutlinedInput
                  autoComplete="off"
                  label="Zone Name"
                  value={value || ""}
                  error={Boolean(error)}
                  helperText={error?.message}
                  maxLength={20}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(val);
                    setValue("zoneName", val);
                    dispatch(setFreehandShapeUponCreation({
                      id: freehandShapeUponCreation?.id || zone.id,
                      name: val,
                      type: freehandShapeUponCreation?.type || ZoneType.TEMPORARY,
                      polygonCoordinates: freehandShapeUponCreation?.polygonCoordinates || [],
                    }));
         
                  }}
                />
              )}
            />

            <Controller
              name="zoneDescription"
              control={control}
              rules={{
                maxLength: { value: 200, message: "Maximum 200 characters allowed" },
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <ArcOutlinedInput
                  autoComplete="off"
                  label="Zone Description"
                  value={value || ""}
                  error={Boolean(error)}
                  helperText={error?.message}
                  maxLength={200}
                  multiline
                  onChange={onChange}
                />
              )}
            />
          </ArcStack>
        </ArcStack>
      </ArcStack>

      <ArcStack spacing={2} px={1}>
        <ArcStack direction='row' spacing={2}>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onCancel}> 
          Cancel 
          </ArcButton.ghost>

          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onClear}> 
          Clear Zone
          </ArcButton.ghost>
        </ArcStack>
        <ArcButton.brand 
          fullWidth
          size='large'
          type='submit'
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}> 
          Apply
        </ArcButton.brand>
      </ArcStack>
 
    </ArcStack>
  );
};